<template>
    <vx-table-row>
        <div
            class="rounded-t-2xl col-span-full flex flex-wrap items-center gap-y-1 gap-x-2 -mx-6 -mt-6 mb-2 p-4 bg-slate-50 border-b border-solid border-slate-100 sm:px-6 sm:py-4 xl:hidden"
            data-test="mobile-header"
        >
            <button
                type="button"
                class="cursor-pointer flex items-center order-2 sm:order-1 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                data-test="toggle-favorite-mobile"
                aria-label="Favorite"
                :aria-pressed="orgUnit.is_favorite"
                @click="toggleFavorite()"
            >
                <font-awesome-icon
                    widthAuto
                    v-if="orgUnit.is_favorite"
                    :icon="faStar"
                    class="text-amber-400"
                ></font-awesome-icon>
                <font-awesome-icon widthAuto v-else :icon="faStarReg" class="text-slate-500"></font-awesome-icon>
            </button>
            <div class="font-bold text-slate-700 text-sm flex-1 min-w-0 truncate order-1 sm:order-2">
                {{ orgUnit.name }}
            </div>
        </div>

        <vx-table-row-title class="max-xl:hidden">
            <div class="flex items-center gap-3">
                <button
                    type="button"
                    class="cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
                    data-test="toggle-favorite"
                    aria-label="Favorite"
                    :aria-pressed="orgUnit.is_favorite"
                    @click="toggleFavorite()"
                >
                    <font-awesome-icon
                        widthAuto
                        v-if="orgUnit.is_favorite"
                        :icon="faStar"
                        class="text-amber-400"
                    ></font-awesome-icon>
                    <font-awesome-icon widthAuto v-else :icon="faStarReg" class="text-slate-500"></font-awesome-icon>
                </button>
                <div class="text-xs sm:text-sm wrap-break-word max-w-[200px]">
                    <vx-badge color="slate" size="xs">
                        {{ orgUnit.id }}
                    </vx-badge>
                    {{ orgUnit.name }}
                </div>
            </div>
        </vx-table-row-title>

        <vx-table-row-item data-test="team">
            <template v-slot:label> Team: </template>
            <kiosk-team-cell
                :teamId="orgUnit.team_id"
                :teamName="orgUnit.team_name"
                :teamStatus="orgUnit.team_status"
                :teamStatusReason="orgUnit.team_status_reason"
            />
        </vx-table-row-item>

        <vx-table-row-item data-test="billable">
            <template v-slot:label> Billable: </template>
            <vx-badge :color="orgUnit.is_billable ? 'success' : 'slate'" size="xs">
                {{ orgUnit.is_billable ? 'Yes' : 'No' }}
            </vx-badge>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-dropdown placement="right">
                <vx-button
                    data-test="dropdown"
                    aria-label="OU actions"
                    size="sm"
                    type="button"
                    color="muted"
                    outline
                    class="w-9"
                >
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item :href="`/kiosk/teams/${orgUnit.team_id}/org-units/${orgUnit.id}`">
                        View
                        <font-awesome-icon widthAuto :icon="faEye"></font-awesome-icon>
                    </vx-dropdown-item>

                    <vx-dropdown-item v-if="orgUnit.is_favorite" @click="toggleFavorite()">
                        Remove Favorite
                        <font-awesome-icon widthAuto :icon="faStar"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-else @click="toggleFavorite()">
                        Favorite
                        <font-awesome-icon widthAuto :icon="faStarReg"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-popover hover placement="top" arrow>
                <vx-button
                    :href="`/kiosk/users/${orgUnit.team_owner_id}/impersonate?team_id=${orgUnit.team_id}&org_unit_id=${orgUnit.id}`"
                    :disabled="impersonateDisabled"
                    size="sm"
                    data-test="impersonate"
                    outline
                    color="muted"
                    class="w-full xl:w-9"
                    aria-label="Impersonate"
                >
                    <span class="xl:hidden">Impersonate</span>
                    <font-awesome-icon widthAuto :icon="faMasksTheater" />
                </vx-button>
                <template v-slot:content>
                    <span>Impersonate</span>
                </template>
            </vx-popover>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import { faEllipsis, faEye, faStar, faMasksTheater } from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarReg } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { adminOrgUnitsClient } from '~/services';
import { computed, ref } from 'vue';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import KioskTeamCell from '~/components/kiosk/KioskTeamCell.vue';

const orgUnit = defineModel({
    required: true,
    type: Object,
});

const currentUserId = Spark.state.user.id;

const impersonateDisabled = computed(
    () => orgUnit.value.team_owner_id === currentUserId || orgUnit.value.team_status === 'closed'
);

const toasted = useToasted();

const togglingFavorite = ref(false);
const toggleFavorite = async () => {
    if (togglingFavorite.value) {
        return;
    }
    togglingFavorite.value = true;
    try {
        if (orgUnit.value.is_favorite) {
            await adminOrgUnitsClient.deleteFavoriteOrgUnit(orgUnit.value.team_id, orgUnit.value.id);
            orgUnit.value.is_favorite = false;
            toasted.success(
                `${orgUnit.value.id} - ${orgUnit.value.name} has been removed from your favorite organizational units.`
            );
        } else {
            await adminOrgUnitsClient.favoriteOrgUnit(orgUnit.value.team_id, orgUnit.value.id);
            orgUnit.value.is_favorite = true;
            toasted.success(
                `${orgUnit.value.id} - ${orgUnit.value.name} has been added to your favorite organizational units.`
            );
        }
    } catch (e) {
        console.error(e);
        if (e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }

        toasted.error(e.response?.data?.message || 'Something went wrong.');
    } finally {
        togglingFavorite.value = false;
    }
};
</script>
